import { useState } from "react";
import type React from "react";
import type { DataSource, SessionSummaryDto } from "@gazelab/shared";
import { INTERRUPTION_LABELS, SELF_REPORTED_VISION_LABELS, VISION_CORRECTION_LABELS, type InterruptionReason, type SelfReportedVision, type VisionCorrection } from "@gazelab/shared";
import { api, errorMessage } from "../../services/api.js";
import { useAsync } from "../../lib/useAsync.js";
import { fmtDate, fmtNum, fmtPct, SESSION_STATUS_LABELS } from "../../lib/format.js";
import { Badge, ConfirmDialog, ErrorAlert, QualityBadge, SourceBadge, Spinner } from "../../components/ui/index.js";
import { Icon } from "../../components/ui/Icon.js";
import { Drawer, NoticeStrip, Segmented } from "../../components/ui/Layout.js";
import { useStudy } from "./StudyLayout.js";
import { DEMO_LOCK_NOTE, useDemo } from "./AdminLayout.js";

type SourceFilter = DataSource | "all";

function interruptionLabel(r: string | null) {
  if (!r) return "—";
  return INTERRUPTION_LABELS[r as InterruptionReason] ?? r;
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-slate-500">{label}</dt>
      <dd className="min-w-0 text-slate-900">{children}</dd>
    </>
  );
}

function SessionDetail({ studyId, sessionId }: { studyId: string; sessionId: string }) {
  const detail = useAsync(() => api.getSession(studyId, sessionId), [studyId, sessionId]);
  const images = useAsync(() => api.listImages(studyId), [studyId]);
  const name = (id: string) => images.data?.find((i) => i.id === id)?.filenameOriginal ?? id.slice(0, 8);
  const d = detail.data;
  if (detail.error) return <ErrorAlert error={detail.error} />;
  if (!d) return <Spinner />;
  return (
    <div className="space-y-5 text-sm">
      <section>
        <h3 className="eyebrow mb-2">Participante</h3>
        <dl className="grid grid-cols-[9rem_1fr] gap-x-3 gap-y-1">
          <DetailRow label="Fuente">
            <SourceBadge source={d.source} />
          </DetailRow>
          <DetailRow label="Estado">{SESSION_STATUS_LABELS[d.status]}</DetailRow>
          <DetailRow label="Edad / grupo">
            {d.ageYears} años · {d.groupCode ?? "sin grupo"}
          </DetailRow>
          <DetailRow label="Lentes">{VISION_CORRECTION_LABELS[d.visionCorrection as VisionCorrection] ?? d.visionCorrection}</DetailRow>
          <DetailRow label="Visión">{SELF_REPORTED_VISION_LABELS[d.selfReportedVision as SelfReportedVision] ?? d.selfReportedVision}</DetailRow>
          <DetailRow label="Consentimiento">
            {d.consentVersion} · {fmtDate(d.consentedAt)}
          </DetailRow>
          <DetailRow label="Semilla de orden">
            <span className="font-mono text-xs">{d.orderSeed}</span>
          </DetailRow>
          {d.failureReason && <DetailRow label="Motivo de fallo">{d.failureReason}</DetailRow>}
        </dl>
      </section>

      <section>
        <h3 className="eyebrow mb-2">Dispositivo y contexto visual</h3>
        <dl className="grid grid-cols-[9rem_1fr] gap-x-3 gap-y-1">
          <DetailRow label="Pantalla / viewport">
            {d.deviceMeta.screenWidth}×{d.deviceMeta.screenHeight} · {d.deviceMeta.viewportWidth}×{d.deviceMeta.viewportHeight} CSS
          </DetailRow>
          <DetailRow label="DPR / zoom">
            {d.deviceMeta.devicePixelRatio} · {fmtNum(d.deviceMeta.estimatedZoom, 2)}
          </DetailRow>
          <DetailRow label="Iluminación">{d.deviceMeta.lighting}</DetailRow>
          <DetailRow label="Distancia">{d.deviceMeta.distanceCm ? `${d.deviceMeta.distanceCm} cm` : "—"}</DetailRow>
          <DetailRow label="Pantalla completa">{d.deviceMeta.fullscreenGranted ? "Sí" : "No"}</DetailRow>
          <DetailRow label="Modo de prueba">{d.deviceMeta.isTestMode ? "Sí" : "No"}</DetailRow>
          <DetailRow label="Navegador">
            <span className="block truncate text-xs" title={d.deviceMeta.userAgent}>
              {d.deviceMeta.browserSupported ? "Compatible" : "No verificado"} · {d.deviceMeta.userAgent}
            </span>
          </DetailRow>
        </dl>
      </section>

      <section>
        <h3 className="eyebrow mb-2">Calibraciones y validaciones ({d.calibrations.length})</h3>
        {d.calibrations.length === 0 ? (
          <p className="text-slate-500">Sin calibraciones registradas.</p>
        ) : (
          <ol className="space-y-2">
            {d.calibrations.map((c) => (
              <li key={c.id} className="rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold">
                    #{c.sequence} · {c.phase === "initial" ? "Inicial" : "Revalidación"}
                  </span>
                  <QualityBadge value={c.classification} override={c.manualOverride} />
                </div>
                <p className="mt-1 text-slate-600">
                  Error {fmtNum(c.metrics.meanErrorPx, 0)} / {fmtNum(c.metrics.medianErrorPx, 0)} px ({fmtPct(c.metrics.meanErrorRelative)} diag.) · {fmtNum(c.metrics.percentInRoi, 0)} % en radio ·{" "}
                  {fmtNum(c.metrics.samplesPerSecond, 1)} muestras/s · {c.metrics.pointsEvaluated} puntos
                </p>
                <p className="mt-0.5 text-xs text-slate-500" title={c.metrics.method}>
                  Decisión: {c.decision}
                  {c.overrideReason ? ` — ${c.overrideReason}` : ""}
                </p>
              </li>
            ))}
          </ol>
        )}
      </section>

      {d.pursuit && (
        <section data-testid="pursuit-detail">
          <h3 className="eyebrow mb-2">Validación con punto en movimiento</h3>
          <div className="rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200">
            <p>
              Retraso estimado <strong>{d.pursuit.metrics.lagMs === null ? "—" : `${fmtNum(d.pursuit.metrics.lagMs, 0)} ms`}</strong> · error{" "}
              {fmtNum(d.pursuit.metrics.meanErrorPx, 0)} px ({fmtPct(d.pursuit.metrics.meanErrorRelative)} diag.) · correlación {fmtNum(d.pursuit.metrics.correlation, 2)}
            </p>
            <p className="mt-0.5 text-xs text-slate-500">
              {d.pursuit.metrics.totalSamples} muestras · {fmtNum(d.pursuit.metrics.samplesPerSecond, 1)} muestras/s
              {d.pursuit.metrics.lagMs === null && " · no hubo muestras suficientes para estimar el retraso"}
            </p>
          </div>
        </section>
      )}

      <section>
        <h3 className="eyebrow mb-2">Ensayos ({d.trials.length})</h3>
        {d.trials.length === 0 ? (
          <p className="text-slate-500">Sin ensayos guardados.</p>
        ) : (
          <ol className="divide-y divide-slate-100 rounded-xl ring-1 ring-slate-200">
            {d.trials.map((t) => (
              <li key={t.id} className="flex items-start gap-3 px-3 py-2">
                <span className="w-5 shrink-0 font-util text-xs text-slate-400">{t.sequenceIndex + 1}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium" title={name(t.imageId)}>
                    {name(t.imageId)}
                  </p>
                  <p className="text-xs text-slate-500">
                    {fmtNum(t.durationActualMs, 0)} ms · {t.sampleCountRaw} / {t.sampleCountValid} / {t.sampleCountOnImage} muestras (crudas / válidas / en imagen)
                  </p>
                  {t.video && (
                    <p className="text-xs text-slate-500">
                      Video: {t.video.framesPresented} fotogramas · {t.video.framesDropped ?? "—"} perdidos · {t.video.stallCount} detenciones
                      {t.video.firstFrameOffsetMs !== null && ` · primer fotograma a ${fmtNum(t.video.firstFrameOffsetMs, 0)} ms`}
                      {!t.video.endedNaturally && " · no llegó al final"}
                    </p>
                  )}
                  <p className="font-mono text-[0.7rem] text-slate-400">
                    pintada {fmtNum(t.bounds.displayedLeft, 0)},{fmtNum(t.bounds.displayedTop, 0)} · {fmtNum(t.bounds.displayedWidth, 0)}×{fmtNum(t.bounds.displayedHeight, 0)} · viewport{" "}
                    {t.bounds.viewportWidth}×{t.bounds.viewportHeight} · DPR {t.bounds.devicePixelRatio}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  {t.status === "completed" ? <Badge tone="success">Completado</Badge> : <Badge tone="warning">{t.status === "interrupted" ? "Interrumpido" : "Descartado"}</Badge>}
                  {t.interruptionReason && <div className="mt-0.5 max-w-36 text-xs text-slate-500">{interruptionLabel(t.interruptionReason)}</div>}
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}

export function StudyParticipantsPage() {
  const { study } = useStudy();
  const locked = useDemo()?.enabled === true;
  const [source, setSource] = useState<SourceFilter>("all");
  const sessions = useAsync(() => api.listSessions(study.id, source === "all" ? undefined : source), [study.id, source]);
  const [selected, setSelected] = useState<string | null>(null);
  const [toExclude, setToExclude] = useState<SessionSummaryDto | null>(null);
  const [toDelete, setToDelete] = useState<SessionSummaryDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const list = sessions.data ?? [];
  const [codeQuery, setCodeQuery] = useState("");
  const normalizedQuery = codeQuery.trim().toUpperCase();
  const visible = normalizedQuery ? list.filter((s) => s.participantCode.toUpperCase().includes(normalizedQuery)) : list;
  const hasSynthetic = list.some((s) => s.source === "synthetic") || study.sessionCounts.synthetic.total > 0;

  async function act(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      await sessions.reload();
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  const selectedSession = list.find((s) => s.id === selected) ?? null;
  const completed = list.filter((s) => s.status === "completed").length;
  const excludedCount = list.filter((s) => s.excluded).length;

  return (
    <div className="space-y-3">
      {hasSynthetic && (
        <NoticeStrip tone="synthetic" testId="synthetic-banner">
          <strong>Datos de demostración (SINTÉTICOS):</strong> sesiones generadas por computadora para probar el panel. Nunca se combinan con resultados reales.
        </NoticeStrip>
      )}
      {/* Barra de filtros en una sola línea. */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-64" data-tour="participants-search">
          <label htmlFor="code-search" className="sr-only">
            Buscar por código de participante
          </label>
          <input
            id="code-search"
            className="input pl-3 font-mono"
            placeholder="Buscar código P-XXXXXXXX"
            value={codeQuery}
            onChange={(e) => setCodeQuery(e.target.value)}
            data-testid="participant-code-search"
          />
        </div>
        <div className="w-80" data-tour="participants-source">
          <Segmented
            label="Fuente"
            size="sm"
            value={source}
            onChange={setSource}
            options={[
              { value: "all", label: "Todas" },
              { value: "webgazer", label: "Reales" },
              { value: "synthetic", label: "Sintéticas" },
            ]}
          />
        </div>
        <dl className="ml-auto flex gap-4 text-sm">
          {(
            [
              ["Sesiones", list.length],
              ["Completadas", completed],
              ["Excluidas", excludedCount],
            ] as const
          ).map(([label, value]) => (
            <div key={label} className="text-right">
              <dt className="text-xs text-slate-500">{label}</dt>
              <dd className="font-display text-lg font-bold leading-tight text-ink">{value}</dd>
            </div>
          ))}
        </dl>
      </div>
      <ErrorAlert error={error ?? sessions.error} />
      {sessions.loading && !sessions.data ? (
        <Spinner />
      ) : (
        // La tabla se desplaza dentro de su propio marco (encabezado fijo) en lugar de alargar la página.
        <div className="max-h-[calc(100vh-13rem)] overflow-auto rounded-2xl border border-slate-200 bg-white shadow-sm" data-tour="participants-table">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 z-10 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 shadow-[0_1px_0_0_rgb(226_232_240)]">
              <tr>
                <th className="px-3 py-2">Participante</th>
                <th className="px-3 py-2">Grupo · edad</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2">Fecha</th>
                <th className="px-3 py-2" title="Ensayos completados / interrumpidos">
                  Ensayos
                </th>
                <th className="px-3 py-2">Última validación</th>
                <th className="px-3 py-2">Análisis</th>
                <th className="px-3 py-2">
                  <span className="sr-only">Acciones</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visible.map((s) => (
                <tr
                  key={s.id}
                  className={`cursor-pointer transition-colors ${selected === s.id ? "bg-sky-50" : "hover:bg-slate-50"}`}
                  data-testid="session-row"
                  onClick={() => setSelected(s.id)}
                >
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-medium text-ink">{s.participantCode}</span>
                      <SourceBadge source={s.source} />
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5">
                    {s.groupCode ?? <span className="text-rose-700">sin grupo</span>} · {s.ageYears}
                  </td>
                  <td className="px-3 py-1.5">
                    <span className={s.status === "completed" ? "text-emerald-700" : s.status === "aborted" || s.status === "interrupted" ? "text-amber-700" : ""}>
                      {SESSION_STATUS_LABELS[s.status]}
                    </span>
                    {s.failureReason && <div className="max-w-48 truncate text-xs text-slate-500" title={s.failureReason}>{s.failureReason}</div>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-xs text-slate-600" title={`Inicio ${fmtDate(s.startedAt ?? s.createdAt)} · Fin ${fmtDate(s.completedAt)}`}>
                    {fmtDate(s.startedAt ?? s.createdAt)}
                  </td>
                  <td className="px-3 py-1.5 tabular-nums">
                    {s.trialsCompleted}
                    {s.trialsInterrupted > 0 && <span className="text-amber-700"> · {s.trialsInterrupted} int.</span>}
                  </td>
                  <td className="px-3 py-1.5">
                    {s.lastCalibration ? (
                      <span className="inline-flex items-center gap-1.5">
                        <QualityBadge value={s.lastCalibration.classification} override={s.lastCalibration.manualOverride} />
                        <span className="text-xs text-slate-500">{fmtNum(s.lastCalibration.meanErrorPx, 0)} px</span>
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-3 py-1.5">
                    {s.excluded ? (
                      <span title={s.excluded.reason}>
                        <Badge tone="danger">Excluida</Badge>
                      </span>
                    ) : (
                      <span className="text-xs text-slate-500">Incluible</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex items-center gap-1">
                      {s.excluded ? (
                        <button type="button" className="rounded-full px-2.5 py-1 text-xs font-semibold text-sky-800 hover:bg-sky-50" onClick={() => act(() => api.includeSession(study.id, s.id))}>
                          Reincluir
                        </button>
                      ) : (
                        <button type="button" className="rounded-full px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100" onClick={() => setToExclude(s)}>
                          Excluir
                        </button>
                      )}
                      <button
                        type="button"
                        aria-label="Borrar"
                        title={locked ? DEMO_LOCK_NOTE : "Borrar sesión"}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-500 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-30 disabled:hover:bg-transparent"
                        disabled={locked}
                        onClick={() => setToDelete(s)}
                      >
                        <Icon name="trash" className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-slate-500">
                    No hay sesiones con este filtro.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-slate-500">Haz clic en una fila para ver el detalle de la sesión.</p>
      <Drawer
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={<span className="font-mono">{selectedSession?.participantCode ?? "Sesión"}</span>}
        subtitle={selectedSession ? `${selectedSession.groupCode ?? "sin grupo"} · ${selectedSession.ageYears} años · ${SESSION_STATUS_LABELS[selectedSession.status]}` : undefined}
      >
        {selected && <SessionDetail studyId={study.id} sessionId={selected} />}
      </Drawer>

      <ConfirmDialog
        open={toExclude !== null}
        title={`Excluir sesión ${toExclude?.participantCode ?? ""} del análisis`}
        withReason="Motivo de la exclusión (queda registrado)"
        message={<p>La sesión se conserva pero no aporta a mapas ni métricas. Las exclusiones se aplican por igual a todos los grupos.</p>}
        confirmLabel="Excluir"
        onCancel={() => setToExclude(null)}
        onConfirm={async (reason) => {
          const s = toExclude!;
          setToExclude(null);
          await act(() => api.excludeSession(study.id, s.id, reason));
        }}
      />
      <ConfirmDialog
        open={toDelete !== null}
        title={`Borrar sesión ${toDelete?.participantCode ?? ""}`}
        danger
        requireText={toDelete?.participantCode}
        message={<p>Se borrarán definitivamente la sesión, sus calibraciones, ensayos y muestras de mirada. Los resultados se recalcularán.</p>}
        confirmLabel="Borrar definitivamente"
        onCancel={() => setToDelete(null)}
        onConfirm={async () => {
          const s = toDelete!;
          setToDelete(null);
          if (selected === s.id) setSelected(null);
          await act(() => api.deleteSession(study.id, s.id));
        }}
      />
    </div>
  );
}
